import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { initialResponses, responseTemplates, formatResponseFromTemplate } from './content/TelegramMessages';

// Add sanitize-html for safely rendering HTML content
import DOMPurify from 'dompurify';

// Function to safely render HTML content including links
const renderHTML = (html: string) => {
  return { __html: DOMPurify.sanitize(html) };
};

interface Message {
  id: number;
  sender: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
  toolCalls?: ToolCall[];
  status?: 'sent' | 'delivered' | 'read';
}

interface ToolCall {
  tool: string;
  input?: any;
  output?: any;
  status: 'running' | 'complete' | 'error';
}

interface TelegramInterfaceProps {
  initialMessages?: Message[];
  onSendMessage: (message: string) => void;
  autoDemo?: boolean;
}

// Unused interface commented out
// interface SuggestionButton {
//   text: string;
//   action: string;
// }

const TelegramInterface: React.FC<TelegramInterfaceProps> = ({ initialMessages = [], onSendMessage, autoDemo = true }) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoInProgress, setDemoInProgress] = useState(false);
  const demoRunningRef = useRef(false);
  const demoLoopCountRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<Message[]>(messages);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Keep messagesRef updated with latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  // Sample chats for Telegram sidebar
  const chats = [
    { name: 'AgentBot', message: 'Let me search for that...', time: '11:45 AM', unread: 0, isBot: true, active: true },
    { name: 'Work Chat', message: 'Alex: Meeting at 3pm tomorrow', time: '10:30 AM', unread: 2, isGroup: true },
    { name: 'Sarah', message: 'Thanks for the info!', time: 'Yesterday', unread: 0 },
    { name: 'Dev Team', message: 'Maria: The new feature is live', time: 'Yesterday', unread: 5, isGroup: true },
    { name: 'John', message: 'How\'s the project going?', time: '3/15/24', unread: 0 },
  ];
  
  // Suggestion buttons shown in the UI (empty for now) - commented out to fix unused variable warning
  // const suggestionButtons: SuggestionButton[] = [];

  // Demo messages - sequence of user messages for the auto demo
  const demoMessages = [
    "What's the weather forecast for Moscow this week?",
    "Send me the latest news about cryptocurrency",
    "I need a cab to the airport tomorrow at 10am",
    "Translate 'hello, how are you?' to Russian",
    "Show me nearby restaurants with good reviews"
  ];

  // Predefined tool responses for each demo message
  const demoResponses: Record<string, {tool: string, input: any, output: any}> = {
    "What's the weather forecast for Moscow this week?": {
      tool: 'weather_forecast',
      input: { 
        location: "Moscow", 
        days: 7
      },
      output: { 
        location: "Moscow, Russia",
        forecast: [
          { day: "Monday", condition: "Partly Cloudy", high: 12, low: 4, precipitation: "20%" },
          { day: "Tuesday", condition: "Sunny", high: 15, low: 6, precipitation: "0%" },
          { day: "Wednesday", condition: "Sunny", high: 17, low: 7, precipitation: "0%" },
          { day: "Thursday", condition: "Cloudy", high: 14, low: 8, precipitation: "30%" },
          { day: "Friday", condition: "Rain", high: 10, low: 5, precipitation: "80%" },
          { day: "Saturday", condition: "Rain", high: 9, low: 4, precipitation: "70%" },
          { day: "Sunday", condition: "Partly Cloudy", high: 11, low: 3, precipitation: "20%" }
        ],
        units: "celsius",
        updated: "2024-04-17T08:30:00Z"
      }
    },
    "Send me the latest news about cryptocurrency": {
      tool: 'news_search',
      input: { 
        query: "cryptocurrency", 
        sort: "recent",
        count: 5
      },
      output: { 
        articles: [
          { title: "Bitcoin Surges Past $70,000 in Market Rally", source: "CryptoDaily", published: "Today, 6:45 AM", url: "https://cryptodaily.example.com/bitcoin-surge" },
          { title: "New Regulations Impact Cryptocurrency Exchanges", source: "Financial Times", published: "Today, 5:30 AM", url: "https://ft.example.com/crypto-regulations" },
          { title: "Major Bank Launches Cryptocurrency Trading Platform", source: "Bloomberg", published: "Yesterday", url: "https://bloomberg.example.com/bank-crypto" },
          { title: "Ethereum Update: What Investors Need to Know", source: "CoinDesk", published: "Yesterday", url: "https://coindesk.example.com/ethereum-update" },
          { title: "NFT Market Shows Signs of Recovery After Slump", source: "Reuters", published: "2 days ago", url: "https://reuters.example.com/nft-recovery" }
        ],
        query: "cryptocurrency",
        total_results: 1254,
        language: "en"
      }
    },
    "I need a cab to the airport tomorrow at 10am": {
      tool: 'taxi_booking',
      input: { 
        pickup: "current_location",
        destination: "airport", 
        time: "tomorrow 10:00 AM",
        passengers: 1
      },
      output: { 
        booking_id: "TX-85429",
        service: "TelegramTaxi",
        car_type: "Comfort",
        estimated_price: "1200-1500 RUB",
        pickup_time: "2024-04-18T10:00:00",
        pickup_location: "Saved Home Address",
        destination: "Sheremetyevo International Airport (SVO)",
        estimated_travel_time: "45 min",
        driver_info: {
          arriving_time: "Arriving on time",
          rating: 4.8
        },
        status: "confirmed"
      }
    },
    "Translate 'hello, how are you?' to Russian": {
      tool: 'translator',
      input: { 
        text: "hello, how are you?", 
        source_language: "English",
        target_language: "Russian" 
      },
      output: { 
        translated_text: "привет, как дела?",
        source_language: "en",
        target_language: "ru",
        phonetic: "privet, kak dela?",
        alternates: [
          "привет, как ты?",
          "здравствуйте, как у вас дела?"
        ],
        formality: "informal"
      }
    },
    "Show me nearby restaurants with good reviews": {
      tool: 'location_search',
      input: { 
        query: "restaurants", 
        location: "near_me",
        filter: "rating > 4" 
      },
      output: { 
        results: [
          { name: "Cafe Pushkin", rating: 4.8, cuisine: "Russian", price: "₽₽₽", distance: "0.8 km", address: "Tverskoy Blvd, 26А", open_now: true },
          { name: "White Rabbit", rating: 4.7, cuisine: "Modern European", price: "₽₽₽₽", distance: "1.2 km", address: "Smolenskaya Sq, 3", open_now: true },
          { name: "Mari Vanna", rating: 4.5, cuisine: "Russian", price: "₽₽", distance: "1.5 km", address: "Spiridonievskiy Ln, 10А", open_now: true },
          { name: "Sakhalin", rating: 4.6, cuisine: "Seafood", price: "₽₽₽₽", distance: "2.1 km", address: "Smolenskaya St, 8", open_now: false }
        ],
        current_location: "Moscow City Center",
        query_time: "2024-04-17T15:45:20Z",
        total_matches: 24
      }
    }
  };

  // Fisher-Yates shuffle algorithm for randomizing message order
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Auto demo functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoDemo && !demoComplete && !demoInProgress && !demoRunningRef.current && (messages.length === 0 || demoLoopCountRef.current > 0)) {
      setDemoInProgress(true);
      demoRunningRef.current = true;
      runDemoSequence();
    }
  }, [autoDemo, messages.length, demoComplete, demoInProgress]);
  
  // Disabled exhaustive-deps for this effect because runDemoSequence would create a circular reference

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Use scrollTop to control scroll position within container only
      const scrollHeight = chatContainerRef.current.scrollHeight;
      chatContainerRef.current.scrollTop = scrollHeight;
    }
  };

  // Function to simulate typing and sending a message
  const simulateTyping = (message: string) => {
    return new Promise<void>((resolve) => {
      // Clear any existing text
      setInputValue('');
      
      // Simulate typing character by character
      let currentCharIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentCharIndex <= message.length) {
          setInputValue(message.substring(0, currentCharIndex));
          currentCharIndex++;
        } else {
          clearInterval(typingInterval);
          
          // Small pause after typing before sending
          setTimeout(() => {
            // Call a specific version of send that uses the current inputValue
            handleSendDemoMessage(message);
            resolve();
          }, 500);
        }
      }, 10 + Math.random() * 10); // Fast typing speed for demos
    });
  };

  // Special version of handleSendMessage specifically for the demo
  const handleSendDemoMessage = (demoMessage: string) => {
    if (!demoMessage.trim()) return;
    
    // Create a unique message ID to prevent duplicates
    const messageId = Date.now();
    
    // Create and add the user message
    const userMessage: Message = {
      id: messageId,
      sender: 'user',
      text: demoMessage,
      timestamp: new Date(),
      status: 'sent'
    };
    
    // Only add this message if it doesn't already exist
    setMessages(prev => {
      // Check if this message already exists
      const exists = prev.some(m => m.text === demoMessage && m.sender === 'user');
      if (exists) return prev;
      return [...prev, userMessage];
    });
    
    setInputValue('');
    
    // Simulate message delivery status updates
    setTimeout(() => {
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated.find(m => m.id === messageId);
        if (lastUserMessage) {
          lastUserMessage.status = 'delivered';
        }
        return [...updated];
      });
    }, 500);
    
    // Simulate agent responding with the predetermined response for this demo message
    setTimeout(() => {
      // Update status to read
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated.find(m => m.id === messageId);
        if (lastUserMessage) {
          lastUserMessage.status = 'read';
        }
        return [...updated];
      });
      
      setIsTyping(true);
    }, 1000);
    
    // Simulate agent processing time
    const processingTime = 1000 + Math.random() * 1000;
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Use predefined responses for the demo
      const demoResponse = demoResponses[demoMessage];
      if (!demoResponse) return;
      
      // Initial response text based on tool type
      const initialResponseText = initialResponses[demoResponse.tool] || "I'm working on that for you...";
      
      const responseId = Date.now() + 1;
      const botResponse: Message = {
        id: responseId,
        sender: 'agent',
        text: initialResponseText,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: demoResponse.tool,
            input: demoResponse.input,
            status: 'running',
          }
        ]
      };
      
      // Only add this message if the user message exists and this bot response doesn't
      setMessages(prev => {
        // Check if we already have this response 
        const userExists = prev.some(m => m.text === demoMessage && m.sender === 'user');
        const botExists = prev.some(m => 
          m.sender === 'agent' && 
          m.text === initialResponseText &&
          m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
        );
        
        if (!userExists || botExists) return prev;
        return [...prev, botResponse];
      });
      
      scrollToBottom();
      
      // Tools always run and complete successfully
      const toolCompletionTime = 1500 + Math.random() * 1000;
      
      setTimeout(() => {
        setMessages(prev => {
          const updatedMessages = [...prev];
          // Find the exact message by comparing content rather than just ID
          const lastMessage = updatedMessages.find(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText &&
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
          );
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = demoResponse.output;
            
            // Update the response text
            lastMessage.text = formatResponseFromTemplate(
              responseTemplates[toolCall.tool] || "I've completed your request. Here are the results.",
              demoResponse.output,
              toolCall.tool
            );
          }
          
          return updatedMessages.map(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText && 
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool) && 
            lastMessage ? lastMessage : m
          );
        });
        
        scrollToBottom();
      }, toolCompletionTime);
    }, processingTime);
  };

  // Run the full demo sequence
  const runDemoSequence = async () => {
    // Increment loop counter
    demoLoopCountRef.current++;
    console.log(`Starting Telegram demo loop ${demoLoopCountRef.current}/1`);
    
    // Wait for initial render
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Randomize the order of messages for this loop
    const randomizedMessages = shuffleArray(demoMessages);
    
    // Loop through each demo message
    for (let i = 0; i < randomizedMessages.length; i++) {
      // Wait for previous message to be processed
      if (i > 0) {
        // Wait for agent to finish responding to previous message
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            // Use messagesRef to get the latest messages
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            
            if (lastMessage && lastMessage.sender === 'agent' && 
                (!lastMessage.toolCalls || 
                 lastMessage.toolCalls.every(tc => tc.status === 'complete'))) {
              clearInterval(checkInterval);
              // Add some delay between conversations
              setTimeout(resolve, 2000);
            }
          }, 500);
        });
      }
      
      // Type and send the next message
      await simulateTyping(randomizedMessages[i]);
    }
    
    // Only run a single loop, then mark as complete
    demoRunningRef.current = false;
    setDemoInProgress(false);
    setDemoComplete(true);
    
    // Switch back to the first platform (Slack) to complete the cycle
    const switchPlatformEvent = new CustomEvent('switchPlatform', {
      detail: { platform: 'slack' }
    });
    window.dispatchEvent(switchPlatformEvent);
  };

  const handleSendMessage = useCallback((text = inputValue) => {
    if (text.trim() === '') return;

    // Add user message
    const newMessage: Message = {
      id: Date.now(),
      sender: 'user',
      text: text,
      timestamp: new Date(),
      status: 'sent'
    };

    setMessages(prev => [...prev, newMessage]);
    onSendMessage(text);
    setInputValue('');

    // Update message status after a delay (sent -> delivered -> read)
    setTimeout(() => {
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated[updated.length - 1];
        if (lastUserMessage.sender === 'user') {
          lastUserMessage.status = 'delivered';
        }
        return updated;
      });
    }, 1000);

    // Simulate agent typing
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated[updated.length - 1];
        if (lastUserMessage.sender === 'user') {
          lastUserMessage.status = 'read';
        }
        return updated;
      });

      setIsTyping(false);
      
      // Randomly select a Telegram-appropriate tool
      const telegramTools = ['weather_forecast', 'news_search', 'taxi_booking', 'translator', 'location_search'];
      const selectedTool = telegramTools[Math.floor(Math.random() * telegramTools.length)];
      
      // Create tool-specific response text and mock data
      let initialResponse = "I'll help with that!";
      let mockInput = {};
      let mockOutput = {};
      
      switch (selectedTool) {
        case 'weather_forecast':
          initialResponse = "Let me check the weather for you...";
          mockInput = { 
            location: "Current Location", 
            days: 5
          };
          mockOutput = { 
            location: "New York, USA",
            forecast: [
              { day: "Today", condition: "Sunny", high: 22, low: 15, precipitation: "5%" },
              { day: "Tomorrow", condition: "Partly Cloudy", high: 20, low: 14, precipitation: "15%" },
              { day: "Friday", condition: "Cloudy", high: 18, low: 13, precipitation: "40%" },
              { day: "Saturday", condition: "Rain", high: 16, low: 12, precipitation: "80%" },
              { day: "Sunday", condition: "Partly Cloudy", high: 19, low: 13, precipitation: "30%" }
            ],
            units: "celsius",
            updated: new Date().toISOString()
          };
          break;
          
        case 'news_search':
          initialResponse = "I'll find some news articles about that...";
          mockInput = { 
            query: "technology trends", 
            sort: "relevance",
            count: 3
          };
          mockOutput = { 
            articles: [
              { title: "AI Revolution: How Machine Learning is Transforming Industries", source: "TechReview", published: "Today, 10:15 AM", url: "https://techreview.example.com/ai-revolution" },
              { title: "Quantum Computing Reaches New Milestone", source: "ScienceDaily", published: "Yesterday", url: "https://sciencedaily.example.com/quantum-milestone" },
              { title: "The Future of Remote Work Technologies", source: "BusinessInsider", published: "2 days ago", url: "https://businessinsider.example.com/remote-work-tech" }
            ],
            query: "technology trends",
            total_results: 842,
            language: "en"
          };
          break;
          
        case 'taxi_booking':
          initialResponse = "I'll arrange transportation for you...";
          mockInput = { 
            pickup: "current_location",
            destination: "downtown", 
            time: "now",
            passengers: 1
          };
          mockOutput = { 
            booking_id: `TX-${Math.floor(Math.random() * 100000)}`,
            service: "TelegramRide",
            car_type: "Standard",
            estimated_price: "$15-18",
            pickup_time: "ASAP",
            pickup_location: "Current Location",
            destination: "Downtown",
            estimated_travel_time: "15 min",
            driver_info: {
              arriving_time: "Driver arriving in 3 minutes",
              rating: 4.9
            },
            status: "confirmed"
          };
          break;
          
        case 'translator':
          initialResponse = "I'll translate that for you...";
          mockInput = { 
            text: "I would like to order food", 
            source_language: "English",
            target_language: "Spanish" 
          };
          mockOutput = { 
            translated_text: "Me gustaría pedir comida",
            source_language: "en",
            target_language: "es",
            phonetic: "meh goos-tah-REE-ah peh-DEER koh-MEE-dah",
            alternates: [
              "Quisiera ordenar comida",
              "Deseo pedir algo de comer"
            ],
            formality: "neutral"
          };
          break;
          
        case 'location_search':
        default:
          initialResponse = "Let me find what's nearby...";
          mockInput = { 
            query: "coffee shops", 
            location: "near_me",
            filter: "open_now" 
          };
          mockOutput = { 
            results: [
              { name: "Espresso Lane", rating: 4.5, price: "$$", distance: "0.3 miles", address: "123 Main St", open_now: true },
              { name: "Bean Brewery", rating: 4.7, price: "$", distance: "0.5 miles", address: "456 Oak Ave", open_now: true },
              { name: "Caffè Artisan", rating: 4.9, price: "$$$", distance: "0.8 miles", address: "789 Elm Blvd", open_now: true }
            ],
            current_location: "Central District",
            query_time: new Date().toISOString(),
            total_matches: 12
          };
          break;
      }
      
      // This would normally come from your API
      const botResponse: Message = {
        id: Date.now() + 1,
        sender: 'agent',
        text: initialResponse,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: selectedTool,
            input: mockInput,
            status: 'running'
          }
        ]
      };
      
      setMessages(prev => [...prev, botResponse]);
      
      // Simulate tool call completing
      setTimeout(() => {
        setMessages(prev => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = mockOutput;
            
            // Update the response text with the appropriate template
            lastMessage.text = formatResponseFromTemplate(
              responseTemplates[toolCall.tool] || "I've completed your request. Here are the results.",
              mockOutput,
              toolCall.tool
            );
          }
          
          return updated;
        });
      }, 2000);
    }, 2500);
  }, [inputValue, onSendMessage]);

  // Unused function commented out
  // const handleSuggestionClick = (text: string) => {
  //   handleSendMessage(text);
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatPhoneTime = () => {
    return currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const statusIcon = (status?: 'sent' | 'delivered' | 'read') => {
    if (status === 'sent') {
      return (
        <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      );
    } else if (status === 'delivered') {
      return (
        <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.5 12.75l6 6 9-13.5" />
          <path d="M20 6L8.5 17.5l-4-4" />
        </svg>
      );
    } else if (status === 'read') {
      return (
        <svg className="w-3.5 h-3.5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.5 12.75l6 6 9-13.5" />
          <path d="M20 6L8.5 17.5l-4-4" />
        </svg>
      );
    }
    return null;
  };

  // Unused function commented out
  // const toggleSidebar = () => {
  //   setShowSidebar(!showSidebar);
  // };

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-center mx-auto">
      {/* Phone frame */}
      <div className="relative w-[380px] h-[700px] bg-black rounded-[40px] p-3 shadow-xl flex flex-col overflow-hidden">
        {/* Phone notch */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[150px] h-[30px] bg-black rounded-b-[20px] z-20 flex justify-center items-end">
          <div className="w-[80px] h-[10px] bg-[#333] rounded-[5px] mb-1"></div>
        </div>
        
        {/* Phone content */}
        <div className="flex-1 bg-[#121212] rounded-[32px] overflow-hidden flex flex-col">
          {/* iOS Status bar */}
          <div className="bg-black px-5 py-1 flex items-center justify-between text-white text-sm">
            <div>{formatPhoneTime()}</div>
            <div className="flex items-center space-x-1">
              <div className="flex space-x-px">
                <div className="h-3 w-[5px] bg-white rounded-sm"></div>
                <div className="h-2 w-[5px] bg-white rounded-sm mt-1"></div>
                <div className="h-1.5 w-[5px] bg-white rounded-sm mt-1.5"></div>
                <div className="h-1 w-[5px] bg-white rounded-sm mt-2"></div>
              </div>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
              </svg>
              <div className="font-semibold">53%</div>
            </div>
          </div>
          
          {/* Telegram header */}
          <div className="bg-[#17212b] px-3 py-2 flex items-center justify-between text-white">
            <div className="flex items-center">
              <button className="mr-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="currentColor"/>
                </svg>
              </button>
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full overflow-hidden mr-2 bg-[#5cadec] flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-medium text-xs">AgentBot</h2>
                  <p className="text-[9px] text-gray-400">Online</p>
                </div>
              </div>
            </div>
            <div className="flex space-x-5 text-white">
              <button>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.23 15.26L16.69 14.97C16.08 14.9 15.48 15.11 15.05 15.54L13.21 17.38C10.38 15.94 8.06 13.63 6.62 10.79L8.47 8.94C8.9 8.51 9.11 7.91 9.04 7.3L8.75 4.78C8.63 3.77 7.78 3.01 6.76 3.01H5.03C3.9 3.01 2.96 3.95 3.03 5.08C3.56 13.62 10.39 20.44 18.92 20.97C20.05 21.04 20.99 20.1 20.99 18.97V17.24C21 16.23 20.24 15.38 19.23 15.26Z" fill="currentColor"/>
                </svg>
              </button>
              <button>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="currentColor"/>
                </svg>
              </button>
              <button>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Telegram sidebar */}
          <AnimatePresence>
            {showSidebar && (
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.2 }}
                className="absolute top-24 left-0 right-0 bg-[#17212b] z-10 shadow-md border-b border-[#0e1621] max-h-[300px] overflow-y-auto rounded-b-lg"
              >
                <div className="py-2">
                  {chats.map((chat, index) => (
                    <div 
                      key={index}
                      className={`flex items-center p-3 border-b border-[#0e1621] hover:bg-[#202b36] cursor-pointer ${chat.name === 'AgentBot' ? 'bg-[#202b36]' : ''}`}
                      onClick={() => setShowSidebar(false)}
                    >
                      <div className={`w-8 h-8 rounded-full mr-3 flex items-center justify-center text-white font-medium overflow-hidden text-xs ${chat.isBot ? 'bg-[#5cadec]' : chat.isGroup ? 'bg-[#f89736]' : 'bg-[#8774e1]'}`}>
                        {chat.name === 'AgentBot' ? 'AB' : chat.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <h3 className="font-medium truncate text-white text-xs">{chat.name}</h3>
                          <span className="text-[10px] text-[#8596a6]">{chat.time}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-xs text-[#8596a6] truncate">{chat.message}</p>
                          {chat.unread > 0 && (
                            <span className="w-4 h-4 bg-[#5cadec] text-white text-[10px] rounded-full flex items-center justify-center">
                              {chat.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Date banner */}
          <div className="bg-[#17212b] border-t border-[#0e1621] px-4 py-2">
            <div className="text-center text-[10px] text-white opacity-70">
              <p>Today</p>
            </div>
          </div>
          
          {/* Chat background with Telegram pattern */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-2 bg-[#0e1621] scroll-smooth" 
            style={{ 
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23192734' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
              overscrollBehavior: 'contain' // Prevent scroll chaining to parent
            }}
            onScroll={(e) => {
              // Prevent scroll propagation to parent
              e.stopPropagation();
            }}
          >
            <div className="space-y-1">
              {/* First welcome message */}
              <div className="flex justify-start">
                <div className="max-w-[85%] relative rounded-lg p-2 bg-[#182533] shadow-sm text-white">
                  <div className="text-[11px] whitespace-pre-wrap">Hello! I'm your Telegram assistant. How can I help you today?</div>
                  <div className="flex justify-end items-center mt-1 space-x-1">
                    <span className="text-[9px] text-[#8596a6]">9:47 PM</span>
                  </div>
                  <div className="absolute left-0 top-0 transform -translate-x-2 translate-y-1 border-8 border-transparent border-r-[#182533]"></div>
                </div>
              </div>
              
              {messages.map((message) => {
                const isUser = message.sender === 'user';
                return (
                  <div 
                    key={message.id} 
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`relative max-w-[85%] rounded-lg p-2 ${isUser ? 'bg-[#2b5278]' : 'bg-[#182533]'} shadow-sm text-white`}
                    >
                      <div 
                        className="text-[11px] whitespace-pre-wrap"
                        dangerouslySetInnerHTML={renderHTML(message.text)}
                      ></div>
                      <div className="flex justify-end items-center mt-1 space-x-1">
                        <span className="text-[9px] text-[#8596a6]">
                          {formatTimestamp(message.timestamp)}
                        </span>
                        {isUser && statusIcon(message.status)}
                      </div>
                      
                      {/* Custom bubble shape */}
                      {isUser ? (
                        <div className="absolute right-0 top-0 transform translate-x-2 translate-y-1 border-8 border-transparent border-l-[#2b5278]"></div>
                      ) : (
                        <div className="absolute left-0 top-0 transform -translate-x-2 translate-y-1 border-8 border-transparent border-r-[#182533]"></div>
                      )}
                      
                      {/* Tool call status indicators - Telegram style */}
                      {message.toolCalls && message.toolCalls.length > 0 && (
                        <div className="mt-1 text-xs">
                          {message.toolCalls.map((toolCall, index) => {
                            // Only show status messages for running tools
                            if (toolCall.status !== 'running') return null;
                            
                            // Get emoji and status text based on tool type
                            const toolEmojis: Record<string, string> = {
                              weather_forecast: "🌤️",
                              news_search: "📰",
                              taxi_booking: "🚕",
                              translator: "🗣️",
                              location_search: "📍"
                            };
                            
                            const emoji = toolEmojis[toolCall.tool] || "⚙️";
                            
                            // Create Telegram-style action messages
                            const actionMessages: Record<string, string> = {
                              weather_forecast: `${emoji} *Getting weather forecast...*`,
                              news_search: `${emoji} *Searching for news...*`,
                              taxi_booking: `${emoji} *Booking your taxi...*`,
                              translator: `${emoji} *Translating...*`,
                              location_search: `${emoji} *Finding nearby places...*`
                            };
                            
                            const statusMessage = actionMessages[toolCall.tool] || 
                              `${emoji} *Processing your request...*`;
                              
                            return (
                              <div key={index} className="italic text-[9px] text-[#8596a6] mt-1">
                                {statusMessage}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="relative max-w-[85%] rounded-lg p-2 bg-[#182533] shadow-sm">
                    <div className="flex space-x-1 p-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8596a6] animate-bounce"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8596a6] animate-bounce delay-75"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8596a6] animate-bounce delay-150"></div>
                    </div>
                    <div className="absolute left-0 top-0 transform -translate-x-2 translate-y-1 border-8 border-transparent border-r-[#182533]"></div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Input area */}
          <div className="bg-[#17212b] p-2 flex items-center">
            <button className="p-1 text-[#8596a6]">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z" fill="currentColor"/>
              </svg>
            </button>
            <div className="flex-1 bg-[#242f3d] rounded-3xl mx-2 flex items-center px-3">
              <input
                ref={inputRef}
                type="text"
                placeholder="Message"
                className="w-full py-2 outline-none bg-transparent text-white text-xs"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <button className="ml-2 text-[#8596a6]">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 11C16.3284 11 17 10.3284 17 9.5C17 8.67157 16.3284 8 15.5 8C14.6716 8 14 8.67157 14 9.5C14 10.3284 14.6716 11 15.5 11Z" fill="currentColor"/>
                  <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
                  <path d="M15.5 11C16.3284 11 17 10.3284 17 9.5C17 8.67157 16.3284 8 15.5 8C14.6716 8 14 8.67157 14 9.5C14 10.3284 14.6716 11 15.5 11Z" fill="currentColor"/>
                  <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
                  <path d="M12 17.5C14.33 17.5 16.31 16.04 17.11 14H6.89C7.69 16.04 9.67 17.5 12 17.5Z" fill="currentColor"/>
                  <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            <button 
              onClick={() => handleSendMessage()}
              disabled={inputValue.trim() === ''}
              className="p-1 text-[#5cadec] disabled:opacity-50">
              {inputValue.trim() === '' ? (
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 15C10.34 15 9 13.66 9 12C9 10.34 10.34 9 12 9C13.66 9 15 10.34 15 12C15 13.66 13.66 15 12 15Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Home button / iPhone-style indicator */}
        <div className="mt-2 w-1/3 h-1 bg-gray-300 rounded-full mx-auto"></div>
      </div>
    </div>
  );
};

export default TelegramInterface;